import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx"; // Import SheetJS for Excel generation
import Pagination from "./Pagination";

const UnassignedTicket = () => {
  const [unassignedTickets, setUnassignedTickets] = useState([]);
  const [assignedTickets, setAssignedTickets] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState("");
  const [selectedTechnician, setSelectedTechnician] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const ticketsPerPage = 20;
  const [modalTicket, setModalTicket] = useState(null);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [bulk_message, setbulk_message] = useState("");

  const [filteredTickets, setFilteredTickets] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); 
  const [downloadStartDate, setDownloadStartDate] = useState("");
  const [downloadEndDate, setDownloadEndDate] = useState("");

  const exportTicketsByDate = () => {
    let ticketsToExport = filteredTickets;

    // Apply date filter if dates are selected
    if (downloadStartDate && downloadEndDate) {
      const start = new Date(downloadStartDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(downloadEndDate);
      end.setHours(23, 59, 59, 999);

      ticketsToExport = ticketsToExport.filter(ticket => {
        const ticketDate = new Date(ticket.timestamp);
        return ticketDate >= start && ticketDate <= end;
      });
    }

    if (ticketsToExport.length === 0) {
      alert("No records found for the selected filters and duration.");
      return;
    }

    const data = ticketsToExport.map(ticket => {
      return Object.keys(ticket).reduce((acc, key) => {
        acc[key] = ticket[key];
        return acc;
      }, {});
    });

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Filtered Tickets");

    // Generate a dynamic filename based on filters
    let fileName = "Tickets_Export";
    if (statusFilter) fileName += `_${statusFilter}`;
    if (downloadStartDate && downloadEndDate) fileName += `_${downloadStartDate}_to_${downloadEndDate}`;
    
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  useEffect(() => {
    fetchUnassignedTickets();
    fetchTechnicians();
}, []);

// Separate effect for filtering
useEffect(() => {
  let updatedTickets = assignedTickets;

  if (searchQuery) {
    const lowerSearchQuery = searchQuery.toLowerCase(); // Normalize for case-insensitive search


    updatedTickets = updatedTickets.filter(ticket =>
        ticket.ticket_id.toLowerCase().includes(lowerSearchQuery) || 
        (ticket.user_mobile && ticket.user_phone.toString().includes(searchQuery)) || 
        (ticket.alternativeMobile && ticket.alternativeMobile.toString().includes(searchQuery)) || 
        ticket.ticket_assigned_to.toLowerCase().includes(lowerSearchQuery)
    );
}


if (statusFilter) {
  if (statusFilter === "Rescheduled") {
    updatedTickets = updatedTickets.filter(ticket => 
      ticket.reschedule_flag === true && ticket.status === "Assigned"
    );
  } else {
    updatedTickets = updatedTickets.filter(ticket => {
      if (statusFilter.toLowerCase() === "pending") {
        return ticket.reschedule_flag === true && ticket.status === "Assigned";
      } else if (statusFilter.toLowerCase() === "assigned") {
        return ticket.reschedule_flag === false && ticket.status === "Assigned";
      } else {
        return ticket.status.toLowerCase() === statusFilter.toLowerCase();
      }
    });
  }
}

  setFilteredTickets(updatedTickets);
}, [searchQuery, statusFilter, assignedTickets]);

  const fetchUnassignedTickets = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get("https://api.mahirefrigeration.com/api/get_all_tickets", config);
      if (response.data.success && Array.isArray(response.data.tickets)) {
        // console.log(response.data)
        const sortedTickets = response.data.tickets.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setUnassignedTickets(sortedTickets.filter(ticket => ticket.ticket_assigned_to === "NA"));
        setAssignedTickets(sortedTickets);
        setFilteredTickets(sortedTickets); 
      } else {
        setUnassignedTickets([]);
        setAssignedTickets([]);
        setFilteredTickets([]);
        setMessage(response.data.message || "No tickets available.");
      }
    } catch (error) {
      setUnassignedTickets([]);
      setAssignedTickets([]);
      setFilteredTickets([]);
      setMessage("Failed to fetch unassigned tickets.");
    } finally {
      setLoading(false);
    }
  };  

  const fetchTechnicians = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get("https://api.mahirefrigeration.com/api/get_all_technicians", config);
      
      if (response.data.success) {
        setTechnicians(response.data.technicians);
      } else {
        setMessage(response.data.message);
      }
    } catch (error) {
      setMessage("Failed to fetch technicians.");
    }
  };

  const assignTicket = async (e) => {
    e.preventDefault();
    if (!selectedTicket || !selectedTechnician) {
      setMessage("Please select both a ticket and a technician.");
      return;
    }
    try {
      const token = sessionStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post("https://api.mahirefrigeration.com/api/assign_ticket", { ticket_id: selectedTicket, technician_email: selectedTechnician }, config);
      
      if (response.data.success) {
        setMessage("Ticket assigned successfully!");
        setTimeout(()=>{
          setMessage("")
        },3000)
        fetchUnassignedTickets();
      } else {
        setMessage(response.data.message);
        setTimeout(()=>{
          setMessage("")
        },3000)
      }
    } catch (error) {
      setMessage("Failed to assign ticket.");
      setTimeout(()=>{
        setMessage("")
      },3000)
    }
  };

  const openModal = (ticket) => {
    setModalTicket(ticket);
  };

  const handleBulkAssign = async () => {
    if (!selectedTechnician || selectedTickets.length === 0) {
      alert("Please select a technician and at least one ticket.");
      return;
    }
  
    try {
      const token = sessionStorage.getItem("token");
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(
        "https://api.mahirefrigeration.com/api/assign_bulk_tickets",
        { ticket_ids: selectedTickets, technician_email: selectedTechnician },
        config
      );
      // console.log("Sending data:", { ticket_ids: selectedTickets, technician_email: selectedTechnician });

      if (response.data.success) {
        setbulk_message("Ticket assigned successfully!");
        setTimeout(()=>{
          setbulk_message("")
        },3000)
        // alert(response.data.message);
        fetchUnassignedTickets(); // Refresh ticket list
        setSelectedTickets([]);
      } else {
        setTimeout(()=>{
          setbulk_message("")
        },3000)
        setbulk_message(response.data.message)
        // alert(response.data.message);
      }
    } catch (error) {
      setbulk_message("Failed to assign tickets.")
      console.error("Failed to assign tickets.");
      setTimeout(()=>{
        setbulk_message("")
      },3000)
    }
  };

  const handleTicketSelection = (ticketId) => {
    setSelectedTickets((prevSelected) =>
      prevSelected.includes(ticketId)
        ? prevSelected.filter((id) => id !== ticketId)
        : [...prevSelected, ticketId]
    );
  };
  const resetFilters = () => {
    setSearchQuery("");
    setStatusFilter("");
    setDownloadStartDate("");
    setDownloadEndDate("");
    setFilteredTickets(assignedTickets);
  };
  
  
  const lastIndex = currentPage * ticketsPerPage;
  const firstIndex = lastIndex - ticketsPerPage;
  const currentTickets = filteredTickets.slice(firstIndex, lastIndex);

  return (
    <div className="container my-2" style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
      {/* Assign Ticket Box */}
      <div className="box" style={{ flex: "1", padding: "20px", background: "#fff", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0,0,0,0.1)" }}>
        <h2 style={{color:"skyblue"}}>Assign Ticket</h2>
        <form onSubmit={assignTicket}>
          <select className="form__field" value={selectedTicket} onChange={(e) => setSelectedTicket(e.target.value)} required>
            <option value="">Select Unassigned Ticket</option>
            {unassignedTickets.map(ticket => (
              <option key={ticket.ticket_id} value={ticket.ticket_id}>{ticket.ticket_id}</option>
            ))}
          </select>
          <select className="form__field" value={selectedTechnician} onChange={(e) => setSelectedTechnician(e.target.value)} required>
            <option value="">Select Technician</option>
            {technicians.map(tech => (
              <option key={tech.email} value={tech.email}>{tech.name} ({tech.email})</option>
            ))}
          </select>
          <div className="mt-3">
          <button type="submit" className="btn" style={{ minWidth: "", border: "1px solid skyblue" }}>Assign Ticket</button>
          </div>
          {message && <p className="message">{message}</p>}
        </form>
      </div>

      {/* Unassigned Tickets Table Box */}
      <div className="box" style={{ width:"100%",flex: "2", padding: "20px", background: "#fff", borderRadius: "8px", boxShadow: "0px 4px 6px rgba(0,0,0,0.1)" }}>
          <div 
  style={{ 
    display: "flex", 
    alignItems: "center", 
    gap: "10px", 
    marginBottom: "10px", 
    flexWrap: "wrap" 
  }} 
  className="bulk-assign-container"
>
  <label style={{ whiteSpace: "nowrap" }}>Select Technician:</label>

  <select 
    value={selectedTechnician} 
    onChange={(e) => setSelectedTechnician(e.target.value)}
    style={{ flex: "1", padding: "10px", borderRadius: "5px", border: "1px solid #ccc" }}
  >
    <option value="">-- Select Technician --</option>
    {technicians.map((tech) => (
      <option key={tech.email} value={tech.email}>
        {tech.name} ({tech.email})
      </option>
    ))}
  </select>
  <button 
    onClick={handleBulkAssign} 
    disabled={selectedTickets.length === 0 || !selectedTechnician}
    style={{ 
      padding: "10px", 
      borderRadius: "5px", 
      border: "1px solid skyblue", 
      backgroundColor: "skyblue", 
      color: "#fff", 
      fontWeight: "bold",
      cursor: selectedTickets.length === 0 || !selectedTechnician ? "not-allowed" : "pointer",
      flexShrink: "0"
    }}
  >
    Assign Selected Tickets
  </button>

  <p style={{ color: "#000", marginTop: "10px", width: "100%" }}>{bulk_message}</p>
  </div>
  <div className="container">
  <div className="row g-2 mb-4">
    <div className="col-12 col-md-auto">
      <input 
        type="date" 
        className="form-control" 
        value={downloadStartDate} 
        onChange={(e) => setDownloadStartDate(e.target.value)}
        title="From Date"
      />
    </div>
    <div className="col-12 col-md-auto">
      <input 
        type="date" 
        className="form-control" 
        value={downloadEndDate} 
        onChange={(e) => setDownloadEndDate(e.target.value)}
        title="To Date"
      />
    </div>
    <div className="col-12 col-md-auto">
      <button 
        onClick={exportTicketsByDate} 
        className="btn btn-primary w-100" 
        style={{ border: "1px solid skyblue", backgroundColor: "skyblue" }}
      >
        Download Tickets
      </button>
    </div>
    
    <div className="col-12 col-md">
      <input 
        type="text" 
        placeholder="Search by Ticket ID or Mobile and Alternative Mobile" 
        className="form-control w-100" 
        value={searchQuery} 
        onChange={(e) => setSearchQuery(e.target.value)} 
      />
    </div>
    
    <div className="col-12 col-md">
      <select 
        className="form-select w-100" 
        value={statusFilter} 
        onChange={(e) => setStatusFilter(e.target.value)}
      >
        <option value="">All Status</option>
        <option value="Completed">Completed</option>
        <option value="Created">Created</option>
        <option value="Assigned">Assigned</option>
        <option value="Cancelled">Cancelled</option>
        <option value="Rescheduled">Rescheduled</option>
      </select>
    </div>

    <div className="col-12 col-md-auto">
      <button className="btn btn-secondary w-100" onClick={resetFilters}>
        Reset
      </button>
    </div>
  </div>
</div>


      

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <div className="table-wrapper" style={{ overflowX: "auto",minWidth:"100%" }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Action</th>
                    <th>Ticket ID</th>
                    <th>Raised By</th>
                    <th>Category</th>
                    {/* <th>Subcategory</th> */}
                    <th>Assigned to</th>
                    <th>Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {currentTickets.length > 0 ? (
                    currentTickets.map((ticket) => (
                      <tr key={ticket.ticket_id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedTickets.includes(ticket.ticket_id)}
                            onChange={() => handleTicketSelection(ticket.ticket_id)}
                          />
                        </td>
                        <td style={{cursor:"pointer"}} onClick={() => openModal(ticket)} data-bs-toggle="modal" data-bs-target="#ticketModal">{ticket.ticket_id}</td>
                        <td style={{cursor:"pointer"}} onClick={() => openModal(ticket)} data-bs-toggle="modal" data-bs-target="#ticketModal">{ticket.ticket_raised_by}</td>
                        <td style={{cursor:"pointer"}} onClick={() => openModal(ticket)} data-bs-toggle="modal" data-bs-target="#ticketModal">{ticket.ticket_category}</td>
                        <td style={{cursor:"pointer"}} onClick={() => openModal(ticket)} data-bs-toggle="modal" data-bs-target="#ticketModal">{ticket.ticket_assigned_to.split("@")[0]}</td>
                        <td style={{cursor:"pointer"}} onClick={() => openModal(ticket)} data-bs-toggle="modal" data-bs-target="#ticketModal">{new Date(ticket.timestamp).toLocaleString()}</td>
                        <td
                          style={{
                            cursor: "pointer",
                            color:
                              ticket.status === "Completed"
                                ? "green"
                                : ticket.status === "Cancelled"
                                ? "red"
                                : ticket.reschedule_flag && ticket.status === "Assigned"
                                ? "orange"
                                : "black",
                          }}
                          onClick={() => openModal(ticket)}
                          data-bs-toggle="modal"
                          data-bs-target="#ticketModal"
                        >
                          {ticket.status === "Completed"
                            ? "Completed"
                            : ticket.status === "Cancelled"
                            ? "Cancelled"
                            : ticket.reschedule_flag && ticket.status === "Assigned"
                            ? "Pending"
                            : ticket.status}
                        </td>


                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7">No tickets found</td>
                    </tr>
                  )}
                </tbody>


              </table>
            </div>
            {/* <div className="pagination">
              {[...Array(Math.ceil(filteredTickets.length / ticketsPerPage)).keys()].map(number => (
                <button 
                  key={number + 1} 
                  onClick={() => setCurrentPage(number + 1)} 
                  className={currentPage === number + 1 ? "active" : ""}
                >
                  {number + 1}
                </button>
              ))}
            </div> */}
           
      <Pagination
      currentPage={currentPage}
      totalItems={filteredTickets.length}
      itemsPerPage={ticketsPerPage}
      onPageChange={setCurrentPage}
    />
          </>
        )}
      </div>



 {/* Bootstrap Modal */}
 <div className="modal fade bd-example-modal-xl" id="ticketModal" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title" style={{color:"skyblue"}}>Ticket Details</h3>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              {modalTicket && (
                <div>
                <div className="row">
                  <div className="col-md-6 mt-2" style={{boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",textAlign:"left"}}>
                  <div className="row">
                   <div className="col-md-4">
                    <p><strong style={{color:"#000000",font:"bold"}}>Ticket ID</strong></p>
                   </div> 
                   <div className="col-md-8">
                     <p style={{color:"grey"}}>{modalTicket.ticket_id}</p>                
                   </div> 
                   </div>
                   <div className="row">
                   <div className="col-md-4">
                     <p><strong style={{color:"#000000",font:"bold"}}>Raised By</strong></p>
                   </div> 
                   <div className="col-md-8">
                     <p style={{color:"grey"}}>{modalTicket.ticket_raised_by}</p>                 
                    </div> 
                   </div> 
                   <div className="row">
                      <div className="col-md-4">
                       <p><strong style={{color:"#000000",font:"bold"}}>Assigned To</strong></p>
                     </div> 
                      <div className="col-md-8">
                       <p style={{color:"grey"}}>{modalTicket.ticket_assigned_to}</p>                   
                      </div> 
                     </div>
                   <div className="row">
                    <div className="col-md-4">
                     <p><strong style={{color:"#000000",font:"bold"}}>Type</strong></p>
                     </div> 
                      <div className="col-md-8">
                      <p style={{color:"grey"}}>{modalTicket.ticket_type}</p>                 
                      </div> 
                     </div>
                     <div className="row">
                      <div className="col-md-4">
                      <p><strong style={{color:"#000000",font:"bold"}}>Query</strong></p>
                      </div> 
                        <div className="col-md-8">
                      <p style={{color:"grey"}}>{modalTicket.ticket_query}</p>                   
                        </div> 
                      </div>
                        <div className="row">
                     <div className="col-md-4">
                      <p><strong style={{color:"#000000",font:"bold"}}>Category</strong></p>
                    </div> 
                     <div className="col-md-8">
                     <p style={{color:"grey"}}> {modalTicket.ticket_category}</p>                   
                     </div> 
                    </div>
                      <div className="row">
                        <div className="col-md-4">
                        <p><strong style={{color:"#000000",font:"bold"}}>Subcategory</strong></p>
                        </div> 
                            <div className="col-md-8">
                            <p style={{color:"grey"}}>{modalTicket.ticket_subcategory}</p>                   
                          </div> 
                        </div>
                        <div className="row">
                        <div className="col-md-4">
                        <p><strong style={{color:"#000000",font:"bold"}}>Initial Amount</strong></p>
                        </div> 
                            <div className="col-md-8">
                            <p style={{color:"grey"}}>{modalTicket.charges}</p>                   
                          </div> 
                        </div>
                        <div className="row">
                        <div className="col-md-4">
                        <p><strong style={{color:"#000000",font:"bold"}}>Final Amount</strong></p>
                        </div> 
                            <div className="col-md-8">
                            <p style={{color:"grey"}}>{modalTicket.final_charge}</p>                   
                          </div> 
                        </div>
                        <div className="row">
                        <div className="col-md-4">
                        <p><strong style={{color:"#000000",font:"bold"}}>Stand Charge</strong></p>
                        </div> 
                            <div className="col-md-8">
                            <p style={{color:"grey"}}>{modalTicket.standCharge}</p>                   
                          </div> 
                        </div>
                        <div className="row">
                        <div className="col-md-4">
                        <p><strong style={{color:"#000000",font:"bold"}}>Fitting Charge</strong></p>
                        </div> 
                            <div className="col-md-8">
                            <p style={{color:"grey"}}>{modalTicket.fittingCharge}</p>                   
                          </div> 
                        </div>

                  </div> 


                   <div className="col-md-6 mt-2" style={{boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",textAlign:"left"}}>
                      {/* <p style={{color:"skyblue"}}>ADDRESS</p> */}
                      <div className="row">
                       <div className="col-md-4">
                       <p><strong style={{color:"#000000",font:"bold"}}>Address</strong></p>
                     </div> 
                      <p style={{color:"grey"}}> {modalTicket.flat}, {modalTicket.area}, {modalTicket.landmark}, {modalTicket.town}, {modalTicket.pincode}</p>                   

                     </div>
                     <div className="row">
                     <div className="col-md-4">
                       <p><strong style={{color:"#000000",font:"bold"}}>Customer Name</strong></p>
                     </div> 
                      <div className="col-md-8">
                       <p style={{color:"grey"}}> {modalTicket.user_name}</p>                   
                      </div> 
                    </div>
                    <div className="row">
                    <div className="col-md-4">
                       <p><strong style={{color:"#000000",font:"bold"}}>Customer Phone</strong></p>
                     </div> 
                      <div className="col-md-8">
                       <p style={{color:"grey"}}> {modalTicket.user_phone}</p>                   
                      </div> 
                     </div>
                    <div className="row">
                    <div className="col-md-4">
                       <p><strong style={{color:"#000000",font:"bold"}}>Alternative Phone</strong></p>
                     </div> 
                      <div className="col-md-8">
                       <p style={{color:"grey"}}> {modalTicket.alternativeMobile}</p>                   
                      </div> 
                     </div>

                   </div> 
                 </div> 

                 <div className="row">
                 <div className="col-md-12 mt-4" style={{boxShadow: "0px 4px 6px rgba(0,0,0,0.1)",textAlign:"left"}}>
                 <p style={{color:"skyblue"}}>AIR CONDITIONERS DETAILS</p>
                    <div className="row">
                     <div className="col-md-4">
                       <p><strong style={{color:"#000000",font:"bold"}}>AC Brand Name</strong></p>
                     </div> 
                      <div className="col-md-8">
                       <p style={{color:"grey"}}> {modalTicket.ac_brand}</p>                   
                      </div> 
                     </div>
                     <div className="row">
                     <div className="col-md-4">
                       <p><strong style={{color:"#0000000",font:"bold"}}>Model Number</strong></p>
                     </div> 
                      <div className="col-md-8">
                       <p style={{color:"grey"}}> {modalTicket.model_number}</p>                   
                      </div> 
                     </div>
                     <div className="row">
                     <div className="col-md-4">
                       <p><strong style={{color:"#000000",font:"bold"}}>Date of Purchase</strong></p>
                     </div> 
                      <div className="col-md-8">
                       <p style={{color:"grey"}}> {modalTicket.date_of_purchase}</p>                   
                      </div> 
                     </div>
                     <div className="row">
                     <div className="col-md-4">
                       <p><strong style={{color:"#000000",font:"bold"}}>Due Date</strong></p>
                     </div> 
                      <div className="col-md-8">
                       <p style={{color:"grey"}}> {new Date(modalTicket.due_timestamp).toLocaleDateString()}</p>                   
                      </div> 
                     </div>
                     <div className="row">
                     <div className="col-md-4">
                       <p><strong style={{color:"#000000",font:"bold"}}>Remark</strong></p>
                     </div> 
                      <div className="col-md-8">
                       <p style={{color:"grey"}}> {modalTicket.ticket_remark}</p>                   
                      </div> 
                     </div>
                     <div className="row">
                     <div className="col-md-4">
                       <p><strong style={{color:"#000000",font:"bold"}}>Rescheduled</strong></p>
                     </div> 
                      <div className="col-md-8">
                       <p style={{color:"grey"}}>{modalTicket.reschedule_flag?"Yes":"No"}</p>                   
                      </div> 
                     </div>
                     <div className="row">
                     <div className="col-md-4">
                       <p><strong style={{color:"#000000",font:"bold"}}>Rescheduled Time</strong></p>
                     </div> 
                      <div className="col-md-8">
                       <p style={{color:"grey"}}>{modalTicket.reschedule_flag?modalTicket.reschedule_time:"NA"}</p>                   
                      </div> 
                     </div>
                     <div className="row">
                     <div className="col-md-4">
                       <p><strong style={{color:"#000000",font:"bold"}}>Status</strong></p>
                     </div> 
                      <div className="col-md-8">
                      <p style={{ color: "grey" }}>
                        {modalTicket.status === "Completed" ? (
                          <span style={{ color: "green" }}>{modalTicket.status}</span>
                        ) : modalTicket.status === "Cancelled" ? (
                          <span style={{ color: "red" }}>{modalTicket.status}</span>
                        ) : modalTicket.status === "Assigned" && modalTicket.reschedule_flag === true ? (
                          <span style={{ color: "orange" }}>Pending</span>
                        ) : modalTicket.status === "Assigned" && modalTicket.reschedule_flag === false ? (
                          <span>{modalTicket.status}</span>
                        ) : (
                          ""
                        )}
                      </p>
                 
                      </div> 
                     </div>
                    
                     <div className="row">
                    <div className="col-md-4">
                      <p><strong style={{color:"#000000",font:"bold"}}>Time:</strong></p>
                     </div> 
                      <div className="col-md-8">
                       <p style={{color:"grey"}}>{new Date(modalTicket.timestamp).toLocaleString()}</p>                   
                      </div> 
                     </div>

                     <div className="row" style={{ maxHeight: "400px" }}> 
                        <div className="col-md-6">
                          {modalTicket.ticket_image_one && (
                            <>
                              <p><strong style={{ color: "#000000", fontWeight: "bold" }}>Image One:</strong></p>
                              <a href={modalTicket.ticket_image_one} target="_blank">
                              <img 
                                src={modalTicket.ticket_image_one} 
                                alt="Ticket Image 1" 
                                className="img-fluid" 
                                style={{ maxHeight: "300px", objectFit: "cover", width: "100%" }} 
                              />
                              </a>
                            </>
                          )}
                        </div>
                        <div className="col-md-6">
                          {modalTicket.ticket_image_two && (
                            <>
                              <p><strong style={{ color: "#000000", fontWeight: "bold" }}>Image Two:</strong></p>
                              <a href={modalTicket.ticket_image_two} target="_blank">
                              <img 
                                src={modalTicket.ticket_image_two} 
                                alt="Ticket Image 2" 
                                className="img-fluid" 
                                style={{ maxHeight: "300px", objectFit: "cover", width: "100%" }} 
                              />
                              </a>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="row" style={{ maxHeight: "400px" }}> 
                        <div className="col-md-6">
                          {modalTicket.ticket_image_three && (
                            <>
                              <p><strong style={{ color: "#000000", fontWeight: "bold" }}>Image Three:</strong></p>
                              <a href={modalTicket.ticket_image_three} target="_blank">
                              <img 
                                src={modalTicket.ticket_image_three} 
                                alt="Ticket Image 1" 
                                className="img-fluid" 
                                style={{ maxHeight: "300px", objectFit: "cover", width: "100%" }} 
                              />
                              </a>
                            </>
                          )}
                        </div>
                        <div className="col-md-6">
                          {modalTicket.ticket_image_four && (
                            <>
                              <p><strong style={{ color: "#000000", fontWeight: "bold" }}>Image Four:</strong></p>
                              <a href={modalTicket.ticket_image_four} target="_blank">
                              <img 
                                src={modalTicket.ticket_image_four} 
                                alt="Ticket Image 2" 
                                className="img-fluid" 
                                style={{ maxHeight: "300px", objectFit: "cover", width: "100%" }} 
                              />
                              </a>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="row" style={{ maxHeight: "400px" }}> 
                        <div className="col-md-6">
                          {modalTicket.ticket_image_five && (
                            <>
                              <p><strong style={{ color: "#000000", fontWeight: "bold" }}>Image Five:</strong></p>
                              <a href={modalTicket.ticket_image_five} target="_blank">
                              <img 
                                src={modalTicket.ticket_image_five} 
                                alt="Ticket Image 1" 
                                className="img-fluid" 
                                style={{ maxHeight: "300px", objectFit: "cover", width: "100%" }} 
                              />
                              </a>
                            </>
                          )}
                        </div>
                        <div className="col-md-6">
                          {modalTicket.ticket_image_six && (
                            <>
                              <p><strong style={{ color: "#000000", fontWeight: "bold" }}>Image Six:</strong></p>
                              <a href={modalTicket.ticket_image_six} target="_blank">
                              <img 
                                src={modalTicket.ticket_image_six} 
                                alt="Ticket Image 1" 
                                className="img-fluid" 
                                style={{ maxHeight: "300px", objectFit: "cover", width: "100%" }} 
                              />
                              </a>
                            </>
                          )}
                        </div>
                      </div>                  
                      <div className="row" style={{ maxHeight: "400px" }}> 
                        <div className="col-md-6">
                          {modalTicket.ticket_image_seven && (
                            <>
                              <p><strong style={{ color: "#000000", fontWeight: "bold" }}>Image Seven:</strong></p>
                              <a href={modalTicket.ticket_image_seven} target="_blank">
                              <img 
                                src={modalTicket.ticket_image_seven} 
                                alt="Ticket Image 1" 
                                className="img-fluid" 
                                style={{ maxHeight: "300px", objectFit: "cover", width: "100%" }} 
                              />
                              </a>
                            </>
                          )}
                        </div>
                      </div>


                      <div className="row" style={{ maxHeight: "400px" }}>
                      <div className="col-md-6">
                        {modalTicket.ticket_video_one && 
                           <>
                            <p><strong style={{color:"#000000",font:"bold"}}>Video One :</strong></p>
                            {/* <a href={modalTicket.ticket_video_one} target="_blank"> */}
                            <video src={modalTicket.ticket_video_one} controls className="w-100" style={{ height: "300px" }} />
                            {/* </a> */}
                          </>
                        }
                        </div>
                      <div className="col-md-6" style={{height:"100%"}}>
                      {modalTicket.ticket_video_two && 
                     <>
                      <p><strong style={{color:"#000000",font:"bold"}}>Video Two :</strong></p>
                      {/* <a href={modalTicket.ticket_video_two} target="_blank"> */}
                      <video src={modalTicket.ticket_video_two} controls className="w-100" style={{ height: "300px" }} />
                      {/* </a> */}
                     </>
                     }
                      </div>
                      </div>
                  </div>
                 </div> 
                </div>  
              )}
            </div>
          </div>
        </div>
      </div>





    </div>
  );
};

export default UnassignedTicket;